import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ConfirmationDraft } from '../types/agui.types';
import type {
  ApprovalFailure,
  ApproveOptions,
} from '../hooks/use-assistant-chat';
import { resolveLanguage, staticLabel, isRtl } from '../helpers/card-labels';
import {
  importLabel,
  importButtonLabel,
  importTitleParts,
  invitationWarning,
  planNotice,
  truncatedNotice,
  unreadableNotice,
  includeRowLabel,
  pageOfLabel,
  defaultFieldCaption,
} from '../helpers/import-labels';
import {
  cellText,
  defaultSelection,
  duplicateRoute,
  excludedRows,
  fieldTones,
  filterRows,
  importColumns,
  importCounts,
  invitationCount,
  nameColumn,
  paginate,
  problemSentence,
  rowChip,
  rowName,
  selectableRows,
  selectedCount,
  type ChipTone,
  type ImportBatch,
  type ImportCounts,
  type ImportFilter,
  type ImportRow,
} from '../helpers/import-display';
import type { TiaReaction } from './tia/tia-avatar';
import { TiaThreadAvatar } from './tia/tia-thread-avatar';

/**
 * One approval for a whole document import (spec §9): the draft's `import`
 * rows in a table the user can untick, a count strip that filters it, the
 * batch notices, and a primary button that names how many records will be
 * created. Approve sends the unticked selectable rows as `excludedRows`
 * (spec §7.3); with nothing unticked it approves exactly like any card.
 *
 * Selection lives here only: `/pending` brings the card back with the
 * server's default, and `approvalFailure` (a 400 the server returned before
 * running anything) restores the selection the user had.
 */

const ROW_TONE: Record<ChipTone, string> = {
  neutral: '',
  amber: 'bg-amber-50',
  grey: 'bg-gray-50',
  red: 'bg-red-50',
  plan: 'bg-gray-50',
};

const CHIP_TONE: Record<ChipTone, string> = {
  neutral: 'bg-primary-50 text-primary-1000',
  amber: 'bg-amber-100 text-amber-800',
  grey: 'bg-gray-200 text-gray-700',
  red: 'bg-red-100 text-red-700',
  plan: 'border border-dashed border-gray-400 text-gray-700',
};

const CELL_OUTLINE: Record<ChipTone, string> = {
  neutral: '',
  amber: 'outline outline-2 -outline-offset-2 outline-amber-400',
  grey: 'outline outline-2 -outline-offset-2 outline-gray-400',
  red: 'outline outline-2 -outline-offset-2 outline-red-400',
  plan: 'outline outline-2 -outline-offset-2 outline-gray-400',
};

const COUNT_KEYS: Array<{
  key: keyof ImportCounts;
  label: Parameters<typeof importLabel>[1];
}> = [
  { key: 'ready', label: 'countReady' },
  { key: 'warning', label: 'countWarning' },
  { key: 'duplicates', label: 'countDuplicates' },
  { key: 'invalid', label: 'countInvalid' },
  { key: 'over_plan_limit', label: 'countOverPlanLimit' },
];

function initialSelection(
  batch: ImportBatch,
  failure?: ApprovalFailure | null
): Set<number> {
  const selection = defaultSelection(batch);
  for (const row of failure?.excludedRows ?? []) selection.delete(row);
  return selection;
}

export function ImportBatchCard({
  draft,
  batch,
  onApprove,
  onReject,
  onReact,
  approvalFailure,
}: {
  draft: ConfirmationDraft;
  batch: ImportBatch;
  onApprove: (options?: ApproveOptions) => void | Promise<void>;
  onReject: () => void | Promise<void>;
  onReact?: (kind: TiaReaction) => void;
  approvalFailure?: ApprovalFailure | null;
}) {
  const language = resolveLanguage(draft.language);
  const rtl = isRtl(language);
  const [selection, setSelection] = useState<Set<number>>(() =>
    initialSelection(batch, approvalFailure)
  );
  const [filter, setFilter] = useState<ImportFilter>('all');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [cancelFailed, setCancelFailed] = useState(false);
  const [error, setError] = useState<string | null>(
    approvalFailure?.message ?? null
  );

  const columns = useMemo(
    () => importColumns(batch, language),
    [batch, language]
  );
  const stickyColumn = nameColumn(batch);
  const counts = useMemo(() => importCounts(batch), [batch]);
  const visible = useMemo(
    () => filterRows(batch.rows, filter, problemsOnly),
    [batch, filter, problemsOnly]
  );
  const current = paginate(visible, page);
  const pageSelectable = current.rows.filter((r) => r.selectable);
  const pageAllSelected =
    pageSelectable.length > 0 &&
    pageSelectable.every((r) => selection.has(r.row));
  const pageSomeSelected = pageSelectable.some((r) => selection.has(r.row));
  const headerRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.indeterminate = pageSomeSelected && !pageAllSelected;
    }
  }, [pageSomeSelected, pageAllSelected]);

  const n = selectedCount(batch, selection);
  const invitations = invitationCount(batch, selection);
  // The backend's `summary` ("Import 7 leads from leads.pdf") is English
  // prose it cannot localise — the title is built from parts instead: the
  // same "Import {n} {noun}" phrase as the button, in the card's language,
  // plus the source filename (never translated) in its own dir="auto"
  // element. This card only renders once `batch` has been read successfully
  // (ConfirmationCard falls back to the key/value card otherwise), so no
  // draft.summary fallback is needed here.
  //
  // The title counts the rows that CAN be imported (selectable), against
  // the card's total row count — not the user's live ticks, which the
  // approve button already shows ("Import 8 of 12 leads from x.pdf" when 4
  // of 12 rows are invalid/duplicate/over the plan limit).
  const importable = selectableRows(batch).length;
  const titleParts = importTitleParts(
    language,
    batch.record_type,
    importable,
    batch.rows.length,
    batch.source
  );

  const toggleRow = (row: number) => {
    setSelection((previous) => {
      const next = new Set(previous);
      if (next.has(row)) next.delete(row);
      else next.add(row);
      return next;
    });
  };

  const togglePage = () => {
    setSelection((previous) => {
      const next = new Set(previous);
      for (const row of pageSelectable) {
        if (pageAllSelected) next.delete(row.row);
        else next.add(row.row);
      }
      return next;
    });
  };

  const selectAllReady = () => {
    setSelection(new Set(selectableRows(batch)));
  };

  const chooseFilter = (next: ImportFilter) => {
    setFilter((previous) => (previous === next ? 'all' : next));
    setPage(0);
  };

  const approve = async () => {
    const excluded = excludedRows(batch, selection);
    onReact?.('ack');
    setConfirmed(true);
    setBusy(true);
    setError(null);
    try {
      // Nothing unticked: approve exactly like any card (no excluded_rows).
      if (excluded.length > 0) await onApprove({ excludedRows: excluded });
      else await onApprove();
    } catch (failure) {
      setConfirmed(false);
      setError(
        failure instanceof Error && failure.message ? failure.message : null
      );
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    onReact?.('blink');
    setBusy(true);
    setCancelFailed(false);
    try {
      await onReject();
    } catch {
      setCancelFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const notices: string[] = [];
  if (batch.truncated) {
    notices.push(
      truncatedNotice(
        language,
        batch.truncated.max_rows + 1,
        batch.truncated.rows_found
      )
    );
  }
  if (batch.unreadable_pages.length > 0) {
    notices.push(unreadableNotice(language, batch.unreadable_pages));
  }
  if (batch.plan) {
    notices.push(planNotice(language, batch.record_type, batch.plan.remaining));
  }

  return (
    <div
      data-slot='import-batch-card'
      className='flex w-full min-w-0 flex-col gap-2'
      dir={rtl ? 'rtl' : undefined}
    >
      <div className='flex items-center gap-1'>
        <TiaThreadAvatar />
        <p
          data-slot='import-title'
          className='text-base text-secondary-1000'
        >
          {titleParts.source ? (
            <>
              {titleParts.lead} <span dir='auto'>{titleParts.source}</span>
            </>
          ) : (
            titleParts.lead
          )}
        </p>
      </div>

      {confirmed ? null : (
        <div className='flex min-w-0 flex-col gap-2 rounded-2xl border border-primary-200 bg-primary-50 p-3 text-sm text-gray-800'>
          {batch.instructions && (
            <p className='text-start'>
              <span className='font-medium'>
                {importLabel(language, 'instructions')}:
              </span>{' '}
              <span dir='auto'>{batch.instructions}</span>
            </p>
          )}
          {batch.defaults.length > 0 && (
            <p className='text-start'>
              <span className='font-medium'>
                {importLabel(language, 'appliedToAll')}:
              </span>{' '}
              {batch.defaults.map((item, index) => (
                <span key={item.field}>
                  {index > 0 && ' · '}
                  {defaultFieldCaption(language, item.field)}:{' '}
                  <span dir='auto'>{item.label}</span>
                </span>
              ))}
            </p>
          )}
          {notices.map((notice) => (
            <p
              key={notice}
              data-slot='import-notice'
              className='rounded-lg bg-amber-50 px-2 py-1 text-start text-amber-800'
            >
              {notice}
            </p>
          ))}

          <div className='flex flex-wrap items-center gap-1'>
            {COUNT_KEYS.map(({ key, label }) => (
              <button
                key={key}
                type='button'
                aria-pressed={filter === key}
                onClick={() => chooseFilter(key)}
                className='rounded-full border border-primary-200 bg-white px-2 py-0.5 text-xs aria-pressed:border-primary-1000 aria-pressed:bg-primary-100'
              >
                {importLabel(language, label)}{' '}
                <span className='font-semibold'>{counts[key]}</span>
              </button>
            ))}
            <label className='ms-auto flex items-center gap-1 text-xs'>
              <input
                type='checkbox'
                checked={problemsOnly}
                onChange={() => {
                  setProblemsOnly((value) => !value);
                  setPage(0);
                }}
              />
              {importLabel(language, 'showProblemsOnly')}
            </label>
          </div>

          <div>
            <button
              type='button'
              onClick={selectAllReady}
              className='text-xs font-medium text-primary-1000 underline'
            >
              {importLabel(language, 'selectAllReady')}
            </button>
          </div>

          <div className='w-full overflow-x-auto rounded-xl border border-primary-200 bg-white'>
            <table className='min-w-max border-collapse text-xs'>
              <thead>
                <tr className='border-b border-primary-200 text-gray-600'>
                  <th className='sticky start-0 z-[1] w-8 min-w-8 bg-white px-2 py-1'>
                    <input
                      ref={headerRef}
                      type='checkbox'
                      aria-label={importLabel(language, 'selectPage')}
                      checked={pageAllSelected}
                      disabled={pageSelectable.length === 0}
                      onChange={togglePage}
                    />
                  </th>
                  <th className='px-2 py-1 text-start font-medium'>
                    {importLabel(language, 'columnRow')}
                  </th>
                  <th className='px-2 py-1 text-start font-medium'>
                    {importLabel(language, 'columnPage')}
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-2 py-1 text-start font-medium ${
                        column.key === stickyColumn
                          ? 'sticky start-8 z-[1] bg-white'
                          : ''
                      }`}
                    >
                      {column.caption}
                    </th>
                  ))}
                  <th className='px-2 py-1 text-start font-medium'>
                    {importLabel(language, 'columnStatus')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {current.rows.map((row) => (
                  <ImportTableRow
                    key={row.row}
                    row={row}
                    columns={columns}
                    recordType={batch.record_type}
                    stickyColumn={stickyColumn}
                    language={language}
                    checked={row.selectable && selection.has(row.row)}
                    onToggle={() => toggleRow(row.row)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {current.pageCount > 1 && (
            <div className='flex items-center justify-between gap-2 text-xs'>
              <button
                type='button'
                disabled={current.page === 0}
                onClick={() => setPage(current.page - 1)}
                className='rounded-lg border border-primary-200 bg-white px-2 py-1 disabled:opacity-50'
              >
                {importLabel(language, 'previousPage')}
              </button>
              <span>
                {pageOfLabel(language, current.page + 1, current.pageCount)}
              </span>
              <button
                type='button'
                disabled={current.page >= current.pageCount - 1}
                onClick={() => setPage(current.page + 1)}
                className='rounded-lg border border-primary-200 bg-white px-2 py-1 disabled:opacity-50'
              >
                {importLabel(language, 'nextPage')}
              </button>
            </div>
          )}
        </div>
      )}

      {error && !confirmed && (
        <p
          role='alert'
          className='text-start text-xs text-red-600'
        >
          {error}
        </p>
      )}
      {cancelFailed && (
        <p className='text-xs text-red-600'>
          {staticLabel(language, 'cancelFailed')}
        </p>
      )}

      {confirmed ? (
        <div className='flex justify-end'>
          <div
            data-slot='confirmation-ack'
            className='rounded-tl-3xl rounded-tr-3xl rounded-bl-3xl rounded-br-none bg-primary-100 px-3 py-2 text-base text-primary-1000'
          >
            {staticLabel(language, 'confirmedBubble')}
          </div>
        </div>
      ) : (
        <div
          data-slot='import-actions'
          className='flex flex-col gap-2'
        >
          {invitations !== null && (
            // Decision 2: the user confirms with this in view, so it sits
            // with the buttons rather than in the scrolling table.
            <p
              data-slot='invitation-warning'
              className='flex items-start gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-start text-sm font-medium text-amber-900'
            >
              <span aria-hidden='true'>⚠</span>
              {invitationWarning(language, invitations)}
            </p>
          )}
          <div className='flex flex-wrap items-center gap-2'>
            <button
              type='button'
              disabled={busy || n === 0}
              onClick={approve}
              className='inline-flex h-12 items-center justify-center rounded-xl bg-primary-1000 px-5 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50'
            >
              {importButtonLabel(language, batch.record_type, n)}
            </button>
            <button
              type='button'
              disabled={busy}
              onClick={cancel}
              className='inline-flex h-12 items-center justify-center rounded-xl border border-primary-1000 bg-transparent px-5 py-3 text-sm font-medium text-primary-1000 transition-opacity hover:opacity-90 disabled:opacity-50'
            >
              {staticLabel(language, 'cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportTableRow({
  row,
  columns,
  recordType,
  stickyColumn,
  language,
  checked,
  onToggle,
}: {
  row: ImportRow;
  columns: Array<{ key: string; caption: string }>;
  recordType: string;
  stickyColumn: string | undefined;
  language: ReturnType<typeof resolveLanguage>;
  checked: boolean;
  onToggle: () => void;
}) {
  const chip = rowChip(row, language);
  const tone = ROW_TONE[chip.tone];
  // Sticky cells need their own background, or the scrolled cells show through.
  const stickyBackground = tone || 'bg-white';
  // Each outlined cell takes its own problem's severity, not the row's.
  const problemTones = fieldTones(
    row,
    recordType,
    columns.map((column) => column.key)
  );
  const sentences = Array.from(
    new Set(row.problems.map((problem) => problemSentence(problem, language)))
  ).filter((sentence) => sentence !== chip.label);
  const route = row.duplicate_of ? duplicateRoute(row.duplicate_of) : null;
  const duplicateLabel =
    typeof row.duplicate_of?.label === 'string'
      ? row.duplicate_of.label.trim()
      : '';

  return (
    <tr
      data-tone={chip.tone}
      className={`border-b border-primary-100 align-top last:border-b-0 ${tone}`}
    >
      <td
        className={`sticky start-0 z-[1] w-8 min-w-8 px-2 py-1 ${stickyBackground}`}
      >
        <input
          type='checkbox'
          aria-label={includeRowLabel(language, row.row, rowName(row))}
          checked={checked}
          disabled={!row.selectable}
          onChange={onToggle}
        />
      </td>
      <td className='px-2 py-1 text-start tabular-nums'>{row.row}</td>
      <td className='px-2 py-1 text-start tabular-nums'>{row.page ?? ''}</td>
      {columns.map((column) => {
        const problemTone = problemTones.get(column.key);
        const sticky = column.key === stickyColumn;
        const value = cellText(row.values[column.key]);
        return (
          <td
            key={column.key}
            data-column={column.key}
            data-problem={problemTone}
            dir='auto'
            // The cell truncates; the full value stays reachable on hover.
            title={value || undefined}
            className={`max-w-48 truncate whitespace-nowrap px-2 py-1 text-start ${
              sticky ? `sticky start-8 z-[1] ${stickyBackground}` : ''
            } ${problemTone ? CELL_OUTLINE[problemTone] : ''}`}
          >
            {value}
          </td>
        );
      })}
      <td className='px-2 py-1 text-start'>
        <div className='flex flex-col items-start gap-0.5'>
          <span
            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${CHIP_TONE[chip.tone]}`}
          >
            {chip.label}
          </span>
          {/* A hidden record (another partner's: id null, label '') gets
           * the plain chip only — no link, no empty name. */}
          {duplicateLabel &&
            (route ? (
              <Link
                to={route}
                dir='auto'
                data-slot='duplicate-label'
                className='text-xs text-primary-1000 underline'
              >
                {duplicateLabel}
              </Link>
            ) : (
              <span
                dir='auto'
                data-slot='duplicate-label'
                className='text-xs text-gray-700'
              >
                {duplicateLabel}
              </span>
            ))}
          {sentences.length > 0 && (
            <ul className='flex flex-col gap-0.5 text-xs text-gray-700'>
              {sentences.map((sentence) => (
                <li key={sentence}>{sentence}</li>
              ))}
            </ul>
          )}
        </div>
      </td>
    </tr>
  );
}
